'use client';

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';
import HeaderPage from "@/components/header-page";
import ActivityCard from "@/components/activity-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, X, ChevronDown, ChevronUp, MapPin, Globe, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

interface ActivityApiResponse {
  activety: { // Changed from events
    current_page: number;
    data: any[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
}

interface FilterOptions {
  destinations: any[];
  // You can add more filter options specific to activities if needed
  // categories: any[];
  // difficulty_levels: any[];
}

export default function Activities() {
  const t = useTranslations();
  const locale = useLocale();
  const { getLocalizedField } = useLocalizedField();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // State for activities data
  const [activities, setActivities] = useState<any[]>([]); // Changed from events
  const [paginationData, setPaginationData] = useState<ActivityApiResponse['activety'] | null>(null); // Changed type
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    destinations: []
    // Initialize other filter options if needed
  });

  // Build query parameters for API
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") {
      params.append("destination_id", selectedDestination);
    }
    
    // Add more activity-specific parameters if needed
    // if (selectedCategory !== "all") params.append("category_id", selectedCategory);
    
    return params;
  };

  // Build URL parameters for UI
  const buildUrlParams = () => {
    const params = new URLSearchParams();
    
    if (currentPage !== 1) params.append("page", currentPage.toString());
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") params.append("destination", selectedDestination);
    
    return params;
  };

  // Update URL when filters change
  const updateUrl = () => {
    if (isInitialLoad) return;
    
    const params = buildUrlParams();
    const queryString = params.toString();
    const newUrl = queryString ? `/activities?${queryString}` : '/activities'; // Changed route
    
    router.push(newUrl, { scroll: false });
  };

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch destinations
        const destinationsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}alldestinations`);
        const destinationsData = await destinationsResponse.json();
        
        const destinationsList: any[] = destinationsData.destinations || [];
        const sortedDestinations = destinationsList.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        // You can fetch additional activity-specific filters here
        // For example: categories, difficulty levels, etc.
        // const categoriesResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}allactivitycategories`);
        // const categoriesData = await categoriesResponse.json();
        
        setFilterOptions({
          destinations: sortedDestinations,
          // Add other filter options here
        });
        setLoadingFilters(false);
      } catch (error) {
        console.error("Error fetching filter options:", error);
        setLoadingFilters(false);
      }
    };
    
    fetchFilterOptions();
  }, []);

  // Initialize state from URL params
  useEffect(() => {
    if (!searchParams || !isInitialLoad) return;
    
    const pageParam = searchParams.get("page");
    const searchParam = searchParams.get("search");
    const destinationParam = searchParams.get("destination");
    
    if (pageParam) {
      const page = parseInt(pageParam);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
      }
    }
    
    if (searchParam) setSearchQuery(searchParam);
    if (destinationParam) setSelectedDestination(destinationParam);
    
    setIsInitialLoad(false);
  }, [searchParams, isInitialLoad]);

  // Update URL when filters change
  useEffect(() => {
    updateUrl();
  }, [currentPage, searchQuery, selectedDestination]);

  // Fetch activities based on filters
  useEffect(() => {
    const fetchActivities = async () => { // Changed function name
      if (isInitialLoad || loadingFilters) return;
      
      setLoading(true);
      try {
        const apiParams = buildQueryParams();
        
        // Fetch data from allactivity API endpoint
        const apiUrl = `${process.env.NEXT_PUBLIC_API_URL}allactivity?${apiParams.toString()}`; // Changed endpoint
        const response = await fetch(apiUrl);
        const data: ActivityApiResponse = await response.json(); // Changed type
        
        setActivities(data.activety.data); // Changed from data.events.data
        setPaginationData(data.activety); // Changed from data.events
      } catch (error) {
        console.error("Error fetching activities:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivities();
  }, [currentPage, searchQuery, selectedDestination, isInitialLoad, loadingFilters]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle destination change
  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle pagination
  const handlePageChange = (url: string | null) => {
    if (!url) return;
    
    try {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get("page");
      if (pageParam) {
        const page = parseInt(pageParam);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error parsing page URL:", error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
    
    router.push('/activities', { scroll: false }); // Changed route
  };

  // Clear single filters
  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearDestination = () => {
    setSelectedDestination("all");
    setCurrentPage(1);
  };

  // Get selected destination name for display
  const getSelectedDestinationName = () => {
    if (selectedDestination === "all") return null;
    const destination = filterOptions.destinations.find(d => d.slug === selectedDestination);
    return destination ? destination.name : selectedDestination;
  };

  // Function to render pagination links
  const renderPaginationLinks = () => {
    if (!paginationData || !paginationData.links) return null;

    return paginationData.links.map((link, index) => {
      if (link.label === "...") {
        return (
          <PaginationItem key={index}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const isPrevious = link.label.includes("previous");
      const isNext = link.label.includes("next");
      
      if (isPrevious) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.prev_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.prev_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (isNext) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.next_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.next_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                </>
              ) : (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      const pageNum = parseInt(link.label);
      if (!isNaN(pageNum)) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(link.url);
              }}
              isActive={link.active}
              className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center text-sm sm:text-base"
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return null;
    });
  };

  // Filter sidebar component
  const FilterSidebar = () => (
    <div className="bg-card rounded-xl p-6 shadow-sm border border-border space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-green-100 rounded-lg"> {/* Changed color */}
          <Globe className="h-5 w-5 text-green-600" /> {/* Changed icon */}
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('activities.filters')}</h2> {/* Changed text */}
      </div>

      {/* Search Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('activities.search')}</h3> {/* Changed text */}
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Input
              placeholder={t('activities.searchPlaceholder')} // Changed placeholder
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-3"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Destination Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('activities.destination')}</h3>
        <Select
          value={selectedDestination}
          onValueChange={handleDestinationChange}
          disabled={loadingFilters}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('activities.allDestinations')}>
              {selectedDestination !== "all"
                ? getSelectedDestinationName()
                : t('activities.allDestinations')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[400px] overflow-y-auto">
            <SelectGroup>
              <SelectItem value="all" className="font-semibold py-3">
                {t('activities.allDestinations')}
              </SelectItem>
              {filterOptions.destinations.map((destination) => (
                <SelectItem
                  key={destination.id}
                  value={destination.slug}
                  className="py-2 text-sm text-gray-600"
                >
                  {destination.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Add more activity-specific filters here if needed */}
      {/* Example: Category Filter */}
      {/* 
      <div>
        <h3 className="text-lg font-semibold mb-2">Category</h3>
        <Select
          value={selectedCategory}
          onValueChange={handleCategoryChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {filterOptions.categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      */}

      {/* Active Filters */}
      {(selectedDestination !== "all" || searchQuery) && (
        <div className="pt-4 border-t space-y-4">
          <h3 className="text-lg font-semibold">{t('activities.activeFilters')}</h3>
          <div className="space-y-2">
            {searchQuery && (
              <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-sm text-blue-700">
                  {t('activities.search')}: "{searchQuery}"
                </span>
                <button
                  onClick={clearSearch}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {selectedDestination !== "all" && (
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md">
                <span className="text-sm text-green-700">
                  {t('activities.destination')}: {getSelectedDestinationName()}
                </span>
                <button
                  onClick={clearDestination}
                  className="text-green-500 hover:text-green-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="w-full"
          >
            {t('activities.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('navigation.activities')} 
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.activities'), href: "/activities" }, 
        ]}
      />
      
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Button and Active Filters */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('activities.filters')}
              {showMobileFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            
            {/* Active filters chips for mobile */}
            <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2">
              {selectedDestination !== "all" && (
                <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm bg-green-100 text-green-800 whitespace-nowrap ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                  {getSelectedDestinationName()}
                  <X className={`h-3 w-3 cursor-pointer ${locale === 'ar' ? 'mr-1' : 'ml-1'}`} onClick={clearDestination} />
                </span>
              )}
              {searchQuery && (
                <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm bg-blue-100 text-blue-800 whitespace-nowrap ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                  "{searchQuery.length > 15 ? searchQuery.substring(0, 15) + '...' : searchQuery}"
                  <X className={`h-3 w-3 cursor-pointer ${locale === 'ar' ? 'mr-1' : 'ml-1'}`} onClick={clearSearch} />
                </span>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          {/* <Card className="mb-4">
            <CardContent className="p-3 sm:p-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className={`absolute top-3 h-4 w-4 text-gray-400 ${locale === 'ar' ? 'right-3' : 'left-3'}`} />
                  <Input
                    placeholder={t('activities.searchPlaceholderMobile')} 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`${locale === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} min-h-[44px]`}
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className={`absolute top-3 ${locale === 'ar' ? 'left-3' : 'right-3'}`}
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
                <Button type="submit" className="min-w-[44px] min-h-[44px] px-3">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card> */}

          {/* Mobile Filters Dropdown */}
          {showMobileFilters && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <FilterSidebar />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters (Desktop) */}
          <div className="hidden lg:block lg:w-1/4">
            <div className="sticky top-24">
              <FilterSidebar />
            </div>
          </div>

          {/* Right Content - Activities List */}
          <div className="lg:w-3/4">
            {/* Desktop Search */}
            <div className="hidden lg:block mb-6 sm:mb-8">
              <form onSubmit={handleSearch} className="relative max-w-md">
                <Search className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 ${locale === 'ar' ? 'right-4' : 'left-4'}`} />
                <Input
                  placeholder={t('activities.searchPlaceholderDesktop')} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${locale === 'ar' ? 'pr-12 pl-10' : 'pl-12 pr-10'} py-4 sm:py-6 text-base sm:text-lg`}
                  dir={locale === 'ar' ? 'rtl' : 'ltr'}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className={`absolute top-1/2 transform -translate-y-1/2 ${locale === 'ar' ? 'left-4' : 'right-4'}`}
                  >
                    <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </button>
                )}
              </form>
            </div>

            {/* Results Count */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground dark:text-white">
                    {loading ? t('activities.loadingActivities') : `${t('activities.found')} ${paginationData?.total || 0} ${t('activities.activitiesCount')}`} {/* Changed text */}
                  </h2>
                  {paginationData && !loading && (
                    <p className="text-foreground dark:text-gray-400">
                      {t('activities.showing')} {paginationData.from} {t('activities.to')} {paginationData.to} {t('activities.of')} {paginationData.total} {t('activities.results')}
                      {paginationData.current_page && ` (${t('activities.page')} ${paginationData.current_page} ${t('activities.of')} ${paginationData.last_page})`}
                    </p>
                  )}
                </div>
                
                {/* Sort Options (optional) */}
                <div className="hidden md:block">
                  <Select defaultValue="popular">
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder={t('activities.sortBy')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="popular">{t('activities.mostPopular')}</SelectItem>
                      <SelectItem value="newest">{t('activities.newestFirst')}</SelectItem>
                      <SelectItem value="oldest">{t('activities.oldestFirst')}</SelectItem>
                      <SelectItem value="az">{t('activities.aToZ')}</SelectItem>
                      {/* Add activity-specific sort options if needed */}
                      {/* <SelectItem value="difficulty">Difficulty</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Activities Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="h-48 w-full rounded-xl" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏔️</div> {/* Changed emoji */}
                <h3 className="text-2xl font-bold mb-2 text-gray-700 dark:text-gray-300">{t('activities.noActivitiesFound')}</h3> {/* Changed text */}
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {t('activities.tryAdjustingFilters')}
                </p>
                <Button onClick={clearFilters} size="lg">
                  {t('activities.clearAllFilters')}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                  {activities.map((activity) => ( // Changed variable name
                    <ActivityCard // Changed component
                      key={activity.id}
                      item={activity}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {paginationData && paginationData.last_page > 1 && (
                  <div className="mt-8 sm:mt-12">
                    <Pagination className="w-full">
                      <PaginationContent className={`flex flex-wrap justify-center gap-1 sm:gap-2 ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                        {renderPaginationLinks()}
                      </PaginationContent>
                    </Pagination>
                    
                    <div className="mt-3 sm:mt-4 text-center text-xs sm:text-sm text-foreground dark:text-gray-400">
                      <p className={locale === 'ar' ? 'font-arabic' : ''}>
                        {t('activities.page')} {paginationData.current_page} {t('activities.of')} {paginationData.last_page}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}